import { useState } from 'react';
import { Container, Button, Card, Grid, Stack, IconButton, Tooltip } from '@mui/material';
import { FilterList as FilterListIcon } from '@mui/icons-material';
import TagsGrid from './components/TagsGrid';
import TagDetailDrawer from './components/TagDetailDrawer';
import TagForm from './components/TagForm';
import TagsFilters from './components/TagsFilters';
import { TagFilters as TagFiltersType } from './types';
import { PATH_DASHBOARD } from 'src/routes/paths';
import useLocales from 'src/careflow/ph-core/internationalization/hook/useLocales';
import Page from 'src/components/Page';
import useSettings from 'src/careflow/ph-core/multi-tenant/setting/useSettings';
import HeaderBreadcrumbs from 'src/components/HeaderBreadcrumbs';
import Iconify from 'src/components/Iconify';
import { TableToolbar } from 'src/careflow/common';
import useGridFilters from 'src/careflow/ph-core/grid/hook/useGridFilters';

export default function TagsList() {
  const { i18n } = useLocales();
  const { themeStretch } = useSettings();
  const [selectedTagId, setSelectedTagId] = useState<string | null>(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingTagId, setEditingTagId] = useState<string | null>(null);

  const initialFilters: TagFiltersType = {
    codeSystem: [],
    resources: [],
  };

  const {
    filters,
    searchTextFieldValue,
    isFilterDrawerOpen,
    onFilterDrawerOpen,
    onFilterDrawerClose,
    onSearchTextFieldChange,
    onApplyFilters,
  } = useGridFilters<TagFiltersType>(initialFilters);

  const handleRowClick = (tagId: string) => {
    setSelectedTagId(tagId);
  };

  const handleNewTag = () => {
    setEditingTagId(null);
    setIsFormOpen(true);
  };

  const handleEditTag = (tagId: string) => {
    setEditingTagId(tagId);
    setIsFormOpen(true);
  };

  const handleSearch = (value: string) => {
    onSearchTextFieldChange(value);
  };

  // Check if filters are active
  const hasActiveFilters = (filters.codeSystem && filters.codeSystem.length > 0) || 
                           (filters.resources && filters.resources.length > 0);

  return (
    <Page title={i18n('tags.list', 'crs') || 'Tags List'}>
      <Container maxWidth={themeStretch ? false : 'xl'}>
        <HeaderBreadcrumbs
          title={i18n('tags.list', 'crs') || 'Tags List'}
          heading=""
          links={[
            { name: i18n('admin.list.dashboard') || 'Dashboard', href: PATH_DASHBOARD.root },
            { name: i18n('breadcrumbs.title', 'crs') || 'Care Coordination' },
            { name: i18n('tags.list', 'crs') || 'Tags List' },
          ]}
          action={
            <Button
              variant="contained"
              startIcon={<Iconify icon="eva:plus-fill" />}
              onClick={handleNewTag}
              sx={{
                backgroundColor: '#4caf50',
                '&:hover': {
                  backgroundColor: '#45a049',
                },
              }}
            >
              {i18n('newTag', 'crs') || 'New Tag'}
            </Button>
          }
        />

        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card>
              <Stack direction="row" sx={{ py: 2, px: 2 }}>
                <Grid xs={6}>
                  <TableToolbar filterMrn={searchTextFieldValue || ''} onFilterMrn={handleSearch} />
                </Grid>
                <Grid container alignItems="center" justifyContent="flex-end" xs={6}>
                  <Tooltip title="Filter">
                    {hasActiveFilters ? (
                      <IconButton
                        onClick={() => {
                          onFilterDrawerOpen();
                        }}
                        sx={{ height: '40px', backgroundColor: '#7ac25b' }}
                      >
                        <FilterListIcon htmlColor="#087736" />
                      </IconButton>
                    ) : (
                      <IconButton
                        onClick={() => {
                          onFilterDrawerOpen();
                        }}
                        sx={{ height: '40px' }}
                      >
                        <FilterListIcon htmlColor="#8f9993" />
                      </IconButton>
                    )}
                  </Tooltip>
                </Grid>
              </Stack>
              {/* Grid */}
              <TagsGrid
                filters={filters}
                search={searchTextFieldValue || ''}
                onRowClick={handleRowClick}
                onEdit={handleEditTag}
                onFilterDrawerOpen={onFilterDrawerOpen}
                onSearchTextFieldChange={onSearchTextFieldChange}
                searchTextFieldValue={searchTextFieldValue}
                onNewTag={handleNewTag}
              />
            </Card>
          </Grid>
        </Grid>

        {/* Filter Drawer */}
        <TagsFilters
          open={isFilterDrawerOpen}
          onClose={onFilterDrawerClose}
          filters={filters}
          onApplyFilters={onApplyFilters}
        />

        {/* Detail Drawer */}
        {selectedTagId && (
          <TagDetailDrawer
            open={!!selectedTagId}
            tagId={selectedTagId}
            onClose={() => setSelectedTagId(null)}
            onEdit={handleEditTag}
          />
        )}

        {/* Create/Edit Form */}
        {isFormOpen && (
          <TagForm
            open={isFormOpen}
            tagId={editingTagId}
            onClose={() => {
              setIsFormOpen(false);
              setEditingTagId(null);
            }}
          />
        )}
      </Container>
    </Page>
  );
}

